import { isObject } from 'lodash';
import { useMemo } from 'react';

import { ClipType } from '@air/api/types';
import { clipCanOpenInCanvas } from '@air/feature-edit-canvas';

import { useCurrentClipPermissions } from '~/components/AssetModalV2/hooks/useCurrentClipPermissions';
import { type PrivateAddOnFeatures, usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { centralizedClipSelector, centralizedClipTypeSelector } from '~/store/centralizedClip/selectors';
import { canChangeVersionDetails } from '~/utils/permissions/assetPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

export type AssetTransformsConfig = Exclude<PrivateAddOnFeatures['assetTransforms'], boolean | undefined>;

export type UseHasAssetTransformsFlagReturn = {
  hasEditMode: boolean;
  assetTransforms: Partial<AssetTransformsConfig>;
};

export const useHasAssetTransformsFlag = (): UseHasAssetTransformsFlagReturn => {
  const { assetTransforms: assetTransformsAddOn } = usePrivateAddOnFeatures();
  const clipType = useAirSelector(centralizedClipTypeSelector);
  const clip = useAirSelector(centralizedClipSelector);
  const { clipPermissions } = useCurrentClipPermissions();
  const canEdit = canChangeVersionDetails(clipPermissions);
  const isImage =
    clipType === ClipType.livePhoto ||
    ((clipType === ClipType.photo || clipType === ClipType.animated) && !!clip && clipCanOpenInCanvas(clip));

  const assetTransforms = useMemo(() => {
    const result: Partial<AssetTransformsConfig> = {
      ...(isObject(assetTransformsAddOn) && !Array.isArray(assetTransformsAddOn) ? assetTransformsAddOn : {}),
    };

    return result;
  }, [assetTransformsAddOn]);

  const hasEditMode = Object.values(assetTransforms).includes(true) && isImage && canEdit;

  return {
    hasEditMode,
    assetTransforms,
  };
};
